import * as THREE from 'three';
import { PainterContext, TransitionPainter } from '../types';
import { easeInOutCubic } from '../easing';

/**
 * Iris Hole — classic cartoon iris. A fullscreen colored overlay with a
 * circular transparent hole in the middle. On exit the hole shrinks from
 * fully-open to closed (screen fully covered); on enter it expands back.
 * Ported from the nonogram CSS version.
 */
export function createIrisHolePainter(): TransitionPainter {
  let mesh: THREE.Mesh | null = null;
  let material: THREE.ShaderMaterial | null = null;
  let ctxRef: PainterContext | null = null;

  const vertex = /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;

  // Edge is softened over ~2px via fwidth-based smoothstep on the distance.
  const fragment = /* glsl */ `
    precision highp float;
    uniform float uRadius;
    uniform float uAspect;
    uniform vec3 uColor;
    varying vec2 vUv;
    void main() {
      // Plane is sized 2x2 local and scaled by (aspect, 1) in update(), so one
      // unit of vUv.y covers the same world distance as one unit of vUv.x when
      // multiplied by aspect. Undo that here so pixel distances are equal in x/y.
      vec2 p = (vUv - 0.5) * 2.0;
      p.x *= uAspect;
      float d = length(p);
      float edge = fwidth(d);
      float alpha = smoothstep(uRadius - edge, uRadius + edge, d);
      gl_FragColor = vec4(uColor, alpha);
    }
  `;

  return {
    setup(ctx) {
      ctxRef = ctx;
      material = new THREE.ShaderMaterial({
        vertexShader: vertex,
        fragmentShader: fragment,
        transparent: true,
        uniforms: {
          uRadius: { value: 1.5 },
          uAspect: { value: ctx.aspect },
          uColor: { value: new THREE.Vector3(ctx.color.r, ctx.color.g, ctx.color.b) },
        },
      });
      // Plane is 2x2 in local space; we scale by aspect on x in update() so the
      // visible world-space region matches the orthographic frustum exactly.
      const geo = new THREE.PlaneGeometry(2, 2);
      mesh = new THREE.Mesh(geo, material);
      mesh.visible = false;
      ctx.scene.add(mesh);
    },
    update(phase, t, ctx) {
      if (!mesh || !material || !ctxRef) return;
      // Scale x by aspect so the plane fills the ortho frustum precisely.
      mesh.scale.set(ctx.aspect, 1, 1);
      // After aspect correction, corner distance = sqrt(aspect² + 1).
      const maxR = Math.hypot(ctx.aspect, 1) + 0.02;
      const eased = easeInOutCubic(t);
      const progress = phase === 'exit' ? 1 - eased : eased;
      material.uniforms.uRadius.value = progress * maxR;
      material.uniforms.uAspect.value = ctx.aspect;
      material.uniforms.uColor.value.set(ctx.color.r, ctx.color.g, ctx.color.b);
      mesh.visible = true;
    },
    hide() {
      if (mesh) mesh.visible = false;
    },
    dispose() {
      if (mesh) {
        mesh.geometry.dispose();
        (mesh.material as THREE.Material).dispose();
      }
      mesh = null;
      material = null;
      ctxRef = null;
    },
  };
}
